import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { usersAPI, authAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Users, User, Shield, Trash2, Plus, Edit, Eye, EyeOff, Settings, Car, Lock } from "lucide-react";

const AVAILABLE_SECTIONS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "jobs", label: "Jobs" },
  { id: "deliveries", label: "Deliveries" },
  { id: "pdrs", label: "PDR Forms" },
  { id: "job_orders", label: "Job Orders" },
  { id: "customers", label: "Customers" },
  { id: "excavators", label: "Excavators" },
  { id: "parts", label: "Inventory" },
  { id: "invoices", label: "Invoices" },
  { id: "financials", label: "Financials" },
  { id: "settings", label: "Settings" },
  { id: "users", label: "Users" },
];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});  // Track which user passwords are visible
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    role: "mechanic",
    vehicle_number: "",
  });

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          name: formData.name,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        // Only include vehicle_number for drivers
        if (formData.role === "driver") {
          updateData.vehicle_number = formData.vehicle_number || null;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast.success("User updated");
      } else {
        const registerData = { ...formData };
        // Only include vehicle_number for drivers
        if (registerData.role !== "driver") {
          delete registerData.vehicle_number;
        }
        await authAPI.register(registerData);
        toast.success("User created");
      }
      setDialogOpen(false);
      setEditingUser(null);
      setFormData({ username: "", password: "", name: "", role: "mechanic", vehicle_number: "" });
      fetchUsers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : "Operation failed";
      toast.error(errorMsg);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "",
      name: user.name,
      role: user.role,
      vehicle_number: user.vehicle_number || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    try {
      await usersAPI.delete(userId);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openPermissionsDialog = (user) => {
    setSelectedUserForPermissions(user);
    setPermissionsDialogOpen(true);
  };

  const handlePermissionChange = async (sectionId, checked) => {
    if (!selectedUserForPermissions) return;

    const currentPermissions = selectedUserForPermissions.permissions || [];
    let newPermissions;
    
    if (checked) {
      newPermissions = [...currentPermissions, sectionId];
    } else {
      newPermissions = currentPermissions.filter((p) => p !== sectionId);
    }

    try {
      await usersAPI.update(selectedUserForPermissions.id, { permissions: newPermissions });
      setSelectedUserForPermissions({ ...selectedUserForPermissions, permissions: newPermissions });
      setUsers(users.map((u) => 
        u.id === selectedUserForPermissions.id 
          ? { ...u, permissions: newPermissions }
          : u
      ));
      toast.success("Permissions updated");
    } catch (error) {
      toast.error("Failed to update permissions");
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-red-100 text-red-800 border-red-300",
      manager: "bg-blue-100 text-blue-800 border-blue-300",
      mechanic: "bg-slate-100 text-slate-800 border-slate-300",
      driver: "bg-amber-100 text-amber-800 border-amber-300",
    };
    return (
      <Badge className={`${styles[role] || styles.mechanic} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {role}
      </Badge>
    );
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Users
          </h1>
          <p className="text-slate-500">{filteredUsers.length} team members</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingUser(null);
            setFormData({ username: "", password: "", name: "", role: "mechanic", vehicle_number: "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">
                {editingUser ? "Edit User" : "Create New User"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="rounded-sm"
                  required
                  data-testid="user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="johndoe"
                  className="rounded-sm"
                  required
                  data-testid="user-username"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">
                  Password {editingUser ? "(leave blank to keep current)" : "*"}
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? "••••••••" : "Min. 6 characters"}
                    className="rounded-sm pr-10"
                    required={!editingUser}
                    data-testid="user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide">Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v, vehicle_number: v === "driver" ? formData.vehicle_number : "" })}>
                  <SelectTrigger className="rounded-sm" data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mechanic">Mechanic</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role === "driver" && (
                <div className="space-y-2">
                  <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicle Number
                  </Label>
                  <Input
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                    placeholder="e.g., ABC-1234"
                    className="rounded-sm"
                    data-testid="user-vehicle-number"
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-sm">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                  data-testid="user-submit"
                >
                  {editingUser ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="users-search"
        />
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="border-slate-200 hover:border-slate-400 transition-colors">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-800 rounded-sm flex items-center justify-center">
                      <span className="text-blue-500 font-heading font-bold text-xl">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-heading font-bold text-lg text-slate-900">
                          {user.name}
                        </h3>
                        {getRoleBadge(user.role)}
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="rounded-none text-xs">You</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {user.username}
                      </p>
                      {user.role === "driver" && user.vehicle_number && (
                        <p className="text-sm text-amber-600 flex items-center gap-1">
                          <Car className="w-3 h-3" />
                          {user.vehicle_number}
                        </p>
                      )}
                      {user.password_plain ? (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Lock className="w-3 h-3" />
                          <span className="font-mono">
                            {visiblePasswords[user.id] ? user.password_plain : '••••••••'}
                          </span>
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility(user.id)}
                            className="ml-1 text-slate-400 hover:text-slate-600"
                            data-testid={`toggle-password-${user.id}`}
                          >
                            {visiblePasswords[user.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </p>
                      ) : (
                        <p className="text-xs text-orange-500 flex items-center gap-1 mt-1">
                          <Lock className="w-3 h-3" />
                          <span>Password not viewable - edit user to set new password</span>
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        Permissions: {(user.permissions || []).join(", ") || "Default"}
                      </p>
                    </div>
                  </div>
                  {user.id !== currentUser?.id && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPermissionsDialog(user)}
                        className="rounded-sm"
                        data-testid={`permissions-user-${user.id}`}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Permissions
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        className="rounded-sm"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete {user.name}'s account. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Edit Permissions - {selectedUserForPermissions?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Select which sections this user can access:
            </p>
            <div className="space-y-3">
              {AVAILABLE_SECTIONS.map((section) => (
                <div key={section.id} className="flex items-center gap-3">
                  <Checkbox
                    id={`permission-${section.id}`}
                    checked={(selectedUserForPermissions?.permissions || []).includes(section.id)}
                    onCheckedChange={(checked) => handlePermissionChange(section.id, checked)}
                    data-testid={`permission-${section.id}`}
                  />
                  <label
                    htmlFor={`permission-${section.id}`}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {section.label}
                  </label>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setPermissionsDialogOpen(false)}
              className="w-full bg-slate-800 text-white hover:bg-slate-900 rounded-sm"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
